import { Button } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { IconRestore, IconX } from "@tabler/icons-react";
import { useState } from "react";
import { useFormContext } from "react-hook-form";
import { useParams } from "react-router-dom";
import AuthorizationModal from "src/generator/_Modals/AuthorizationModal/AuthorizationModal";
import { useAuthorizationUsers } from "src/generator/hooks/useAuthorizationUsers";
import useCellProps from "src/generator/PortraitOrientation/Row/useCellProps";
import { getFieldNamesForRow } from "src/generator/utils/collectActiveFormFieldNames";
import { refreshLogoutTimer } from "src/generator/utils/refreshLogoutTimer";
import { useIsOnline, usePostApi } from "src/hooks";
import type { ICredentials } from "src/interfaces";
import { useDispatch, useSelector } from "src/redux/hooks";
import { setEditing } from "src/redux/slices/entrySlice";
import {
  removeGuernseyRow,
  setTriggerTimeCheck,
} from "src/redux/slices/generatorFeaturesSlice";
import {
  ESaveEntryReason,
  removeDynamicRow,
  removeVoyageByIndex,
  setSaveReason,
  setSectionProperty,
} from "src/redux/slices/generatorSlice";
import { insertValues } from "src/redux/utils/insertValues";
import { StyledTd } from "src/styled";
import { deepClone } from "src/utils";
import { Notification } from "src/utils/Notification";

import { parseHeadersIntoDoubled } from "../AddCommentsButton/AddCommentsButton.utils";
import { parseContentCols } from "./DeleteRowButton.utils";
import useStrikethroughRow from "./useStrikethroughRow";

interface IAddRowButton {
  rowIndex: number;
  rowNumber: number;
  sectionIndex: number;
  width?: string;
  isAuthorizationRequired?: boolean;
  hidden?: boolean;
  strikethroughRows: boolean | undefined;
  hasStrikethrough: boolean | undefined;
  rowSpan?: number;
}

const DeleteRowButton = ({
  rowIndex,
  rowNumber,
  width,
  sectionIndex,
  isAuthorizationRequired,
  strikethroughRows,
  hasStrikethrough,
  hidden,
  rowSpan,
}: IAddRowButton) => {
  const { getValues, unregister } = useFormContext();
  const dispatch = useDispatch();
  const { post } = usePostApi();
  const isOnline = useIsOnline();
  const [opened, { open, close }] = useDisclosure(false);
  const { logbookId, lbeidOrPartId } = useParams();
  const { propGetter } = useCellProps(Boolean(hidden));
  const handleStrikethrough = useStrikethroughRow({ sectionIndex, rowIndex });

  const { lbentry } = useSelector(state => state.generatorSlice);
  const authorizationUsers = useAuthorizationUsers();
  const { locked, you } = useSelector(state => state.entrySlice);
  const [loading, setLoading] = useState(false);

  const isOfflineDesktop = window.api && !isOnline;

  const regenerateHeaders = () => {
    const newRows: any = structuredClone(lbentry.sections[sectionIndex].rows);
    newRows.splice(rowIndex, 1);
    const newHeaders = parseHeadersIntoDoubled(
      newRows,
      structuredClone(lbentry.sections[sectionIndex].headers),
    );
    dispatch(
      setSectionProperty({
        sectionIndex,
        property: { headers: newHeaders },
      }),
    );
  };

  const unregisterDeletedRowFields = (row: Record<string, any>) => {
    const fieldNames = getFieldNamesForRow(getValues(), row);
    fieldNames.forEach(name => unregister(name));
  };

  const onSuccess = (deletedRow?: Record<string, any>) => {
    if (deletedRow) {
      unregisterDeletedRowFields(deletedRow);
    }
    dispatch(setSaveReason(ESaveEntryReason.deleteRow));
    dispatch(setEditing(false));
    dispatch(
      removeDynamicRow({
        sectionIndex,
        rowIndex,
      }),
    );

    const section = lbentry.sections[sectionIndex];
    const hasVoyageTags =
      section.rows?.[rowIndex]?.cols &&
      Object.values(section.rows[rowIndex].cols).some(
        (col: any) =>
          col.tag &&
          (col.tag === "amb2-date-start" ||
            col.tag === "amb2-time-start" ||
            col.tag === "amb2-date-end" ||
            col.tag === "amb2-time-end"),
      );

    if (hasVoyageTags) {
      dispatch(removeVoyageByIndex(rowIndex));
    }

    dispatch(removeGuernseyRow({ rowIndex }));
    dispatch(setTriggerTimeCheck(Math.random()));
    regenerateHeaders();
    setTimeout(() => dispatch(setEditing(true)), 0);
    refreshLogoutTimer();
    close();
  };
  const handleDeletion = async (userData: ICredentials | null) => {
    const row = lbentry.sections[sectionIndex].rows[rowIndex];
    if (!row) return;
    if (strikethroughRows) {
      handleStrikethrough();
      return;
    }
    setLoading(true);
    if (isOfflineDesktop) {
      onSuccess(row);
      return;
    }

    const modifiedLogbook = insertValues(getValues(), [{ rows: [row] }] as any);
    const rowWithValues = (modifiedLogbook[0] as any).rows[0];

    const parsedRow = parseContentCols(rowWithValues);
    const sectionCopy = deepClone(lbentry.sections[sectionIndex]);

    sectionCopy.rows = [];
    sectionCopy.rows.push(parsedRow);

    sectionCopy.deletedRowIndex = rowIndex;
    sectionCopy.sectionIndex = sectionIndex;

    try {
      await post({
        request: "entry-delete-row",
        body: {
          params: {
            lbid: logbookId,
            rid: lbeidOrPartId,
            row: sectionCopy,
            ...(userData ?? {}),
          },
        },
        onSuccess: () => onSuccess(rowWithValues),
      });
    } catch (e: any) {
      Notification("Error", String(e.message ?? e), "red");
    }
    setLoading(false);
  };

  return (
    <StyledTd
      colSpan={1}
      rowSpan={rowSpan ?? 1}
      valign={rowSpan && rowSpan > 1 ? "middle" : undefined}
      {...propGetter({ width: width ?? "auto" })}
      center>
      <Button
        onClick={async () =>
          isAuthorizationRequired && !isOfflineDesktop
            ? open()
            : await handleDeletion(null)
        }
        variant="outline"
        style={{ margin: "0 auto" }}
        size="xs"
        color={hasStrikethrough ? "green" : "red"}
        disabled={(rowIndex === 0 && rowNumber === 1) || (locked && !you)}
        loading={loading}>
        {hasStrikethrough ? <IconRestore size="18" /> : <IconX size="18" />}
      </Button>
      <AuthorizationModal
        authorizedUserTypes={[7]}
        nonAuthorizedMessage={
          <span>
            You are not permitted to delete rows, select a{" "}
            <span style={{ fontWeight: 200 }}>Yacht Admin</span> to approve this
            on your behalf.
          </span>
        }
        opened={opened}
        close={close}
        onSubmit={handleDeletion}
        loading={loading}
        users={authorizationUsers}
        isEntry={true}
      />
    </StyledTd>
  );
};

export default DeleteRowButton;
